import {
  Alert,
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { getFlatRequirementsBySourcePage } from "Utils/helperFunctions";
import { useBoundStore } from "Zustand/store/mainStore";
import { useEffect, useMemo, useState } from "react";
import { Typography } from "Atoms";
import { Clear, ExpandLess, ExpandMore, Search } from "@mui/icons-material";

import TestReportForm from "./TestReportForm";

type Req = any; // use your Requirement type if you have one
type ReportGroup = {
  nodeid: string;
  name: string;
  branches: Array<{
    nodeid: string;
    name: string;
    requirements: Req[];
  }>;
};

const includesSearch = (value: unknown, query: string) =>
  String(value ?? "").toLowerCase().includes(query);

const highlightSearchMatch = (text: string, query: string) => {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return text;

  const matchIndex = text.toLowerCase().indexOf(trimmedQuery.toLowerCase());
  if (matchIndex < 0) return text;

  // Filtering tells the user what remains; highlighting shows exactly why a
  // tree item matched, matching the Test Requirements search behavior.
  return (
    <>
      {text.slice(0, matchIndex)}
      <Box
        component="span"
        sx={{
          bgcolor: "#fff59d",
          color: "inherit",
          px: 0.25,
          borderRadius: 0.5,
        }}
      >
        {text.slice(matchIndex, matchIndex + trimmedQuery.length)}
      </Box>
      {text.slice(matchIndex + trimmedQuery.length)}
    </>
  );
};

const TestReports = (props:any) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [foundRequirement, setFoundRequirement] = useState<Req | null>(null);
  const [activeView, setActiveView] = useState<"results" | "files">("results");
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [treeSearch, setTreeSearch] = useState("");

  const { selectedTestPlanContent,currentReport } = useBoundStore();

  const getReportPayload = (reportResponse: any) =>
    reportResponse?.testreport ?? reportResponse ?? {};

  const getRequirementReportBySourceNodeId = (
    wrappers: any[],
    requirementNodeId: string
  ) => {
    // Older reports do not have legs yet, so we bridge the source
    // requirement list to its matching report object before rendering details.
    const wrapper = wrappers.find((item: any) =>
      item?.testrequirementreport?.istestrequirementreportof?.some(
        (link: any) => link?.testrequirement?.nodeid === requirementNodeId
      )
    );

    return wrapper?.testrequirementreport ?? null;
  };

  // build tree data only when source changes
  const treedata: ReportGroup[] = useMemo(() => {
    const reportPayload = getReportPayload(currentReport);
    const requirementReportWrappers =
      reportPayload?.hastestrequirementreport ?? [];
    const findRequirementReportByNodeId = (nodeid: string) => {
      const wrapper = requirementReportWrappers.find(
        (item: any) => item?.testrequirementreport?.nodeid === nodeid
      );

      return wrapper?.testrequirementreport ?? null;
    };

    if (Array.isArray(reportPayload?.legs) && reportPayload.legs.length > 0) {
      return reportPayload.legs.map((leg: any, legIndex: number) => ({
        nodeid: `leg-${legIndex}-${leg?.name ?? "unknown"}`,
        name: leg?.name ? `Leg ${leg.name}` : `Leg ${legIndex + 1}`,
        branches: (leg?.branches ?? []).map((branch: any, branchIndex: number) => ({
          nodeid: `leg-${legIndex}-branch-${branchIndex}-${branch?.branch ?? "unknown"}`,
          name: branch?.branch ? `Branch ${branch.branch}` : `Branch ${branchIndex + 1}`,
          requirements: (branch?.tests ?? []).map((test: any) => {
            const reportNodeId = test?.testrequirementreportnodeid ?? "";
            const requirementReport = reportNodeId
              ? findRequirementReportByNodeId(reportNodeId)
              : null;
            const sourceRequirement =
              requirementReport?.istestrequirementreportof?.[0]?.testrequirement;

            return {
              ...sourceRequirement,
              ...test,
              nodeid: reportNodeId,
              name: `${test?.sequence ?? ""} ${test?.testname ?? ""}`.trim(),
              testrequirementreport: requirementReport,
              sourcereference: sourceRequirement?.sourcereference,
            };
          }),
        })),
      }));
    }

    if (selectedTestPlanContent == null) return [];

    const flatRequirements = getFlatRequirementsBySourcePage(
      selectedTestPlanContent
    )
      .map((requirement: any) => {
        const requirementReport = getRequirementReportBySourceNodeId(
          requirementReportWrappers,
          requirement.nodeid
        );

        return requirementReport
          ? {
              ...requirement,
              nodeid: requirementReport.nodeid,
              name: requirement.name,
              testrequirementreport: requirementReport,
            }
          : null;
      })
      .filter(Boolean);

    // Fallback for older reports without execution legs: show test
    // requirements directly, sorted by source-reference page, not by category.
    return [
      {
        nodeid: "test-requirements",
        name: "",
        branches: [
          {
            nodeid: "test-requirements-list",
            name: "",
            requirements: flatRequirements,
          },
        ],
      },
    ];
  }, [selectedTestPlanContent, currentReport]);

  const filteredTreeData = useMemo(() => {
    const query = treeSearch.trim().toLowerCase();
    if (!query) return treedata;

    // Keep the leg/branch hierarchy visible when either the parent label or
    // one of its tests matches the search term.
    return treedata
      .map((group) => {
        const groupMatches = includesSearch(group.name, query);
        const branches = group.branches
          .map((branch) => {
            const branchMatches = includesSearch(branch.name, query);
            const requirements = branch.requirements.filter((requirement: any) =>
              [
                requirement.name,
                requirement.testname,
                requirement.sequence,
                requirement.testid,
              ].some((value) => includesSearch(value, query))
            );

            if (groupMatches || branchMatches) {
              return branch;
            }

            return requirements.length > 0
              ? { ...branch, requirements }
              : null;
          })
          .filter(Boolean) as ReportGroup["branches"];

        return groupMatches || branches.length > 0
          ? { ...group, branches }
          : null;
      })
      .filter(Boolean) as ReportGroup[];
  }, [treedata, treeSearch]);

  useEffect(() => {
    setCollapsedGroups((current) => {
      const next = { ...current };
      treedata.forEach((group) => {
        if (!(group.nodeid in next)) next[group.nodeid] = false;
        group.branches.forEach((branch) => {
          if (!(branch.nodeid in next)) next[branch.nodeid] = false;
        });
      });
      return next;
    });
  }, [treedata]);

  const handleSelect = (id: string) => setSelectedId(id);

  // find the req for the selected id
  useEffect(() => {
    if (!selectedId) {
      setFoundRequirement(null);
      return;
    }
    const req = treedata
      ?.flatMap((leg: any) =>
        (leg.branches ?? []).flatMap((branch: any) => branch.requirements || [])
      )
      ?.find((r: any) => r.nodeid === selectedId);
    setFoundRequirement(req || null);
  }, [selectedId, treedata]);

  // helper: pick first PDF url + page (adapt how you resolve page)
  // const pickPdfFromReq = (req: Req | null) => {
  //   if (!req?.hasattachment?.length) return { url: null, page: 1 };
  //   // your API structure sometimes is [{attachment:{file:{url...}}}]
  //   const entries = req.hasattachment;
  //   // handle either flat or wrapped
  //   const items = entries.map((a: any) => (a.attachment ? a.attachment : a));
  //   const firstPdf = items.find((it: any) =>
  //     /(\.pdf)(\?|$)/i.test(it?.file?.url || "")
  //   );
  //   return {
  //     url: firstPdf?.file?.url ?? null,
  //     page: (req as any)?.sourcePage ?? 1, // change if you keep page somewhere else
  //   };
  // };



  const toggleGroup = (nodeid: string) => {
    setCollapsedGroups((current) => ({ ...current, [nodeid]: !current[nodeid] }));
  };

  return (
    <Box display="flex" gap={2}>
      <Paper
        elevation={3}
        sx={{
          width: { xs: "100%", lg: 360 },
          minHeight: 500,
          p: 2,
          borderRadius: 3,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Test Structure
        </Typography>

        <TextField
          fullWidth
          size="small"
          value={treeSearch}
          onChange={(event) => setTreeSearch(event.target.value)}
          placeholder="Search ..."
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: treeSearch ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  edge="end"
                  size="small"
                  onClick={() => setTreeSearch("")}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />

        <Stack spacing={1.5}>
          {treedata.length === 0 && (
            <Alert severity="info">No test requirements are available for this report.</Alert>
          )}

          {treedata.length > 0 && filteredTreeData.length === 0 && (
            <Alert severity="info">No matching tests found.</Alert>
          )}

          {filteredTreeData.map((group) => (
            <Box key={group.nodeid}>
              {group.name ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 0.5,
                    py: 0.75,
                    borderRadius: 2,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "rgba(0,100,153,0.05)" },
                  }}
                  onClick={() => toggleGroup(group.nodeid)}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "primary.main", fontWeight: 600 }}
                    >
                      {highlightSearchMatch(group.name, treeSearch)}
                    </Typography>
                  </Stack>
                  {collapsedGroups[group.nodeid] && !treeSearch ? <ExpandMore /> : <ExpandLess />}
                </Box>
              ) : null}

              <Collapse in={group.name ? treeSearch ? true : !collapsedGroups[group.nodeid] : true}>
                <Stack spacing={0.5} sx={{ pt: 0.5, pb: 1 }}>
                  {group.branches.map((branch) => (
                    <Box key={branch.nodeid}>
                      {branch.name ? (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            px: 0.75,
                            py: 0.5,
                            ml: 2,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            "&:hover": { bgcolor: "rgba(0,100,153,0.05)" },
                          }}
                          onClick={() => toggleGroup(branch.nodeid)}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: "text.secondary" }}
                          >
                            {highlightSearchMatch(branch.name, treeSearch)}
                          </Typography>
                          {collapsedGroups[branch.nodeid] ? (
                            treeSearch ? (
                              <ExpandLess fontSize="small" />
                            ) : (
                              <ExpandMore fontSize="small" />
                            )
                          ) : (
                            <ExpandLess fontSize="small" />
                          )}
                        </Box>
                      ) : null}

                      <Collapse in={branch.name ? treeSearch ? true : !collapsedGroups[branch.nodeid] : true} sx={{ pl:6}}>
                        <Stack spacing={0.5} sx={{ pt: 0.5, pb: 1 }}>
                          {branch.requirements.map((requirement: any) => {
                            const isSelected = selectedId === requirement.nodeid;
                            return (
                              <Box
                                key={requirement.nodeid}
                                onClick={() => handleSelect(requirement.nodeid)}
                                sx={{
                                  cursor: "pointer",
                                  px: 1,
                                  py: 1,
                                  ml: branch.name ? 3 : group.name ? 2 : 0,
                                  borderRadius: 1.5,
                                  bgcolor: isSelected
                                    ? "rgba(0,100,153,0.12)"
                                    : "transparent",
                                  "&:hover": {
                                    bgcolor: isSelected
                                      ? "rgba(0,100,153,0.16)"
                                      : "rgba(0,0,0,0.03)",
                                  },
                                }}
                              >
                                <Box display="flex" justifyContent="space-between" gap={1}>
                                  <Tooltip title={requirement.name || ""}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: isSelected ? 600 : 400,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: 230,
                                      }}
                                    >
                                      {highlightSearchMatch(
                                        requirement.name || "",
                                        treeSearch
                                      )}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Collapse>
                    </Box>
                  ))}
                </Stack>
              </Collapse>
            </Box>
          ))}
        </Stack>
      </Paper>

      <Paper
        elevation={3}
        sx={{
          minHeight: 500,
          flex: 3,
          p: 3,
          borderRadius: 3,
        }}
      >
        <Tabs
          value={activeView}
          onChange={(_, value) => setActiveView(value)}
          sx={{
            mb: 2,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
            },
          }}
        >
          <Tab label="Test results" value="results" disableRipple disableFocusRipple />
          <Tab label="Files" value="files" disableRipple disableFocusRipple />
        </Tabs>

        {selectedId !== null && foundRequirement ? (
          <TestReportForm
            requirement={foundRequirement}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            reportnodeid={props.reportnodeid}
            resetTree
            viewMode={activeView}
          />
        ) : (
          <Box
            sx={{
              minHeight: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Alert severity="info">
              Select a test from the left tree to view its {activeView === "results" ? "results" : "files"}.
            </Alert>
          </Box>
        )}
      </Paper>

    </Box>
  );
};

export default TestReports;
